import { createDateFormatter, useDisplayTimezone, type TimezoneMode } from "@nexus/ui";
import { positionChartTooltip, useChartLifecycle } from "@nexus/widget-kit";
import { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts/core";
import { LineChart, type LineSeriesOption } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  type GridComponentOption,
  type TooltipComponentOption,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import {
  defineLabels,
  firstError,
  resolveLabels,
  unwrap,
  UpdatingPill,
  Widget,
  WidgetState,
  resolveChartColors,
  withAlpha,
  WidgetSkeleton,
  WidgetIssueState,
} from "@nexus/widget-sdk";
import type { ChartColors, WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import type { DetectionVolumeConfig, DetectionVolumeData } from "./types";

echarts.use([LineChart, GridComponent, TooltipComponent, CanvasRenderer]);

type DetectionVolumeOption = echarts.ComposeOption<
  LineSeriesOption | GridComponentOption | TooltipComponentOption
>;

export type { DetectionVolumeConfig, DetectionVolumeData, DetectionVolumePoint } from "./types";

export const LABELS = defineLabels([
  { key: "emptyTitle", name: "Empty state title", default: "No detections" },
  {
    key: "emptyDescription",
    name: "Empty state description",
    default: "No detection volume data for the selected time range.",
  },
] as const);

export const manifest: WidgetManifest = {
  id: "detection-volume",
  name: "Detection Volume",
  description: "Detection volume over time for the selected time range.",
  version: "1.1.0",
  labels: LABELS,
  dataRequests: {
    volume: {
      source: "detection-volume",
      contract: { name: "detection-volume", version: 1 },
      configurable: false,
    },
  },
};

export const defaultConfig: DetectionVolumeConfig = {
  title: "Detection Volume",
};

/**
 * Optional window metadata the host MAY attach to the volume payload.
 * When present, the chart pins its x-axis to the exact selected range and
 * zero-fills empty buckets, so the curve always spans the full window no
 * matter how sparse the data is. Everything degrades gracefully when these
 * fields are absent (the window/interval is then inferred from the points).
 *
 * To wire it up, add these optional fields to `DetectionVolumeData` in
 * `./types` and populate them from the DataRegistry query.
 */
type VolumeWindowMeta = {
  range?: { from?: string; to?: string };
  bucketMs?: number;
};

const DEFAULT_BUCKET_MS = 5 * 60_000; // last-resort bucket when nothing can be inferred
const MAX_BUCKETS = 5_000; // guard against pathological range/bucket combos
const DAY_MS = 24 * 60 * 60_000;

type SeriesPoint = [number, number]; // [epochMs, count]

type BuiltSeries = {
  data: SeriesPoint[];
  start: number; // axis min
  end: number; // axis max
  nonZeroBuckets: number; // number of real (returned) buckets
  spansDays: boolean;
  hasWindow: boolean; // whether a real window could be established
};

function toMs(value?: string): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function inferBucketMs(sortedMs: number[]): number | null {
  let min = Number.POSITIVE_INFINITY;
  for (let i = 1; i < sortedMs.length; i += 1) {
    const delta = sortedMs[i] - sortedMs[i - 1];
    if (delta > 0 && delta < min) min = delta;
  }
  return Number.isFinite(min) ? min : null;
}

/**
 * Turns a (possibly sparse) list of buckets into a continuous, zero-filled
 * time series so the chart renders a real curve even with 0 or 1 data point,
 * instead of a lone symbol floating in the middle of the plot.
 */
function buildSeries(
  points: DetectionVolumeData["points"] | undefined,
  meta: VolumeWindowMeta | undefined,
): BuiltSeries {
  const parsed: SeriesPoint[] = (points ?? [])
    .map((p): SeriesPoint => [new Date(p.timestamp).getTime(), p.count])
    .filter(([ms]) => Number.isFinite(ms))
    .sort((a, b) => a[0] - b[0]);

  const rangeFrom = toMs(meta?.range?.from);
  const rangeTo = toMs(meta?.range?.to);
  const bucket =
    (meta?.bucketMs && meta.bucketMs > 0 ? meta.bucketMs : null) ??
    inferBucketMs(parsed.map(([ms]) => ms)) ??
    DEFAULT_BUCKET_MS;

  // Determine the window we want to draw: prefer the explicit selected range,
  // fall back to the extent of the returned points.
  let start = rangeFrom ?? (parsed.length ? parsed[0][0] : null);
  let end = rangeTo ?? (parsed.length ? parsed[parsed.length - 1][0] : null);

  if (start === null || end === null) {
    // No data and no range: nothing meaningful to draw → empty state.
    return {
      data: [],
      start: 0,
      end: 0,
      nonZeroBuckets: 0,
      spansDays: false,
      hasWindow: false,
    };
  }

  if (start > end) [start, end] = [end, start];

  // A single instant (or a window narrower than one bucket) would collapse the
  // axis and center a lone symbol with no line. Give it a small window so it
  // reads as an intentional blip on a zero baseline.
  if (end - start < bucket) {
    start -= bucket * 3;
    end += bucket * 3;
  }

  const bucketCount = Math.floor((end - start) / bucket) + 1;
  const lastGrid = start + (bucketCount - 1) * bucket;

  // Snap real points onto the bucket grid (summing any collisions).
  const byBucket = new Map<number, number>();
  for (const [ms, count] of parsed) {
    const slot = start + Math.round((ms - start) / bucket) * bucket;
    byBucket.set(slot, (byBucket.get(slot) ?? 0) + count);
  }

  const overCap = bucketCount > MAX_BUCKETS;
  let data: SeriesPoint[];
  if (overCap) {
    // Window/bucket combo is too fine to fill safely — keep the raw points on
    // the time axis (still correctly positioned, just not zero-filled).
    data = parsed.length
      ? parsed
      : [
          [start, 0],
          [end, 0],
        ];
  } else {
    data = Array.from({ length: bucketCount }, (_, i): SeriesPoint => {
      const t = start + i * bucket;
      return [t, byBucket.get(t) ?? 0];
    });
  }

  const axisEnd = overCap ? end : lastGrid;
  return {
    data,
    start,
    end: axisEnd,
    nonZeroBuckets: parsed.length,
    spansDays: axisEnd - start > DAY_MS,
    hasWindow: true,
  };
}

function formatAxisLabel(ms: number, spansDays: boolean, timezone: TimezoneMode): string {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "";
  if (!spansDays) {
    return createDateFormatter(timezone, {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).format(date);
  }
  const day = createDateFormatter(timezone, { month: "short", day: "numeric" }, "en").format(date);
  const time = createDateFormatter(timezone, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
  return `${day} ${time}`;
}

function formatTooltipDate(ms: number, timezone: TimezoneMode): string {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "";
  return createDateFormatter(
    timezone,
    {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    },
    "en",
  ).format(date);
}

function buildOption(
  series: BuiltSeries,
  colors: ChartColors,
  timezone: TimezoneMode,
): DetectionVolumeOption {
  return {
    useUTC: timezone === "utc",
    animationDuration: 400,
    animationDurationUpdate: 400,
    animationEasing: "cubicOut",
    backgroundColor: "transparent",
    grid: {
      top: 16,
      right: 16,
      bottom: series.spansDays ? 44 : 28,
      left: 44,
      borderWidth: 0,
    },
    tooltip: {
      trigger: "axis",
      appendToBody: true,
      transitionDuration: 0,
      position: (point, _params, _dom, _rect, size) => positionChartTooltip(point, size),
      backgroundColor: colors.tooltipBg,
      borderWidth: 0,
      padding: [8, 10],
      textStyle: {
        color: colors.tooltipText,
        fontSize: colors.typography?.labelSize ?? 12,
        fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
      },
      axisPointer: {
        type: "line",
        lineStyle: { color: colors.grid, width: 1 },
      },
      formatter: (params: unknown) => {
        const items = params as Array<{ data?: SeriesPoint }>;
        const point = items?.[0]?.data;
        if (!Array.isArray(point)) return "";
        const [ms, count] = point;
        return `${formatTooltipDate(ms, timezone)}<br/><b>${count}</b> detection${count === 1 ? "" : "s"}`;
      },
    },
    xAxis: {
      type: "time",
      min: series.start,
      max: series.end,
      axisLabel: {
        hideOverlap: true,
        color: colors.label,
        formatter: (value: number) => formatAxisLabel(value, series.spansDays, timezone),
      },
      axisTick: { show: false },
      axisLine: { show: false },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      min: 0,
      minInterval: 1,
      axisLabel: { color: colors.label },
      axisLine: { show: false },
      axisTick: { show: false },
      // Horizontal split lines only — discreet on dark (--border at low opacity)
      splitLine: { lineStyle: { color: colors.grid } },
    },
    series: [
      {
        type: "line",
        data: series.data,
        smooth: 0.2, // light smoothing avoids cubic overshoot dipping below zero
        // Show markers only when data is sparse, so isolated buckets stay
        // visible; hide them once the line is dense enough to read on its own.
        showSymbol: series.nonZeroBuckets > 0 && series.nonZeroBuckets <= 12,
        symbol: "circle",
        symbolSize: 5,
        sampling: "lttb",
        lineStyle: { width: 1.5, color: colors.operational.detection },
        itemStyle: { color: colors.operational.detection },
        areaStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: withAlpha(colors.operational.detection, 0.08),
              },
              { offset: 1, color: withAlpha(colors.operational.detection, 0) },
            ],
          },
        },
      },
    ],
  };
}

function DetectionVolumeComponent({ data, config }: WidgetProps<DetectionVolumeConfig>) {
  const timezone = useDisplayTimezone();
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const hasRenderedRef = useRef(false);

  const error = firstError(data, ["volume"]);
  const volume = unwrap<DetectionVolumeData>(data.volume);
  const isLoading = data.volume?.isLoading ?? false;

  const title = config.title ?? defaultConfig.title ?? "Detection Volume";
  const labels = resolveLabels(LABELS, config);

  // Optional window metadata (range / bucket size), used if the host provides it.
  const meta = volume as (DetectionVolumeData & VolumeWindowMeta) | undefined;

  const series = useMemo(
    () => buildSeries(volume?.points, meta),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [volume?.points, meta?.range?.from, meta?.range?.to, meta?.bucketMs],
  );

  const isEmpty = !isLoading && !error && series.data.length === 0;
  const showChart = !error && !isEmpty;
  const firstLoad = isLoading && !hasRenderedRef.current;

  // Keep a ref to current series so the theme-change observer closure is fresh.
  const seriesRef = useRef(series);
  useEffect(() => {
    seriesRef.current = series;
  }, [series]);

  // Draw / update the chart. The container stays mounted across states, so
  // there is no init/dispose thrash and refreshes animate smoothly.
  useEffect(() => {
    const host = chartRef.current;
    if (!host || error || isEmpty || isLoading) return;

    if (!chartInstanceRef.current || chartInstanceRef.current.isDisposed()) {
      chartInstanceRef.current = echarts.init(host, null, {
        renderer: "canvas",
      });
    }
    chartInstanceRef.current.setOption(buildOption(series, resolveChartColors(host), timezone));
    hasRenderedRef.current = true;
  }, [series, error, isEmpty, isLoading, timezone]);

  useChartLifecycle(chartRef, chartInstanceRef, (host, chart) => {
    if (hasRenderedRef.current)
      chart.setOption(buildOption(seriesRef.current, resolveChartColors(host), timezone));
  });

  return (
    <Widget title={title} actions={isLoading && !firstLoad ? <UpdatingPill /> : undefined}>
      <div className="relative h-full w-full">
        <div
          ref={chartRef}
          className="h-full w-full transition-opacity duration-200"
          style={{ opacity: showChart ? 1 : 0 }}
        />

        {error && <WidgetIssueState error={error} className="absolute inset-0" />}

        {isEmpty && (
          <WidgetState
            title={labels.emptyTitle}
            description={labels.emptyDescription}
            className="absolute inset-0"
          />
        )}

        {firstLoad && <WidgetSkeleton shape="chart" className="absolute inset-0" />}
      </div>
    </Widget>
  );
}

export const Component = DetectionVolumeComponent;

const widgetModule: WidgetModule<DetectionVolumeConfig> = {
  manifest,
  Component,
  defaultConfig,
};

export default widgetModule;
